import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  useTheme,
  Box,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

const ConfirmDialog = ({
  open,
  onClose,
  onConfirm,
  heading,
  content,
  buttonText,
  loading,
  confirmDisabled = false,
  note,
  maxWidth = "sm",
  minWidth = 400,
  customMaxWidth = 500,
}) => {
  const theme = useTheme();

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={maxWidth}
      PaperProps={{
        sx: {
          borderRadius: 2,
          boxShadow: "0 8px 32px rgba(0,0,0,0.12)",
          minWidth: minWidth,
          maxWidth: customMaxWidth,
        },
      }}
    >
      <DialogTitle
        sx={{
          fontWeight: 600,
          fontSize: "1.25rem",
          color: theme.customColors.text.strong,
          background: theme.customColors.background.cardHeader,
          borderBottom: `1px solid ${theme.customColors.border.main}`,
          pb: 1.5,
          pt: 2,
          px: 3,
        }}
      >
        {heading}
      </DialogTitle>
      <DialogContent
        sx={{
          px: 3,
          py: 3,
          "&.MuiDialogContent-root": {
            paddingTop: 3,
          },
        }}
      >
        {typeof content === "string" ? (
          <Typography
            variant="body2"
            sx={{
              color: theme.customColors.text.secondary,
              lineHeight: 1.6,
              fontSize: "0.9375rem",
            }}
          >
            {content}
          </Typography>
        ) : (
          content
        )}
        {note && (
          <Box
            sx={{
              mt: 2,
              px: 2,
              py: 1.5,
              backgroundColor: theme.customColors.background.info,
              border: `1px solid ${theme.customColors.border.subtle}`,
              borderRadius: "8px",
            }}
          >
            <Typography
              variant="body2"
              sx={{
                color: theme.customColors.text.secondary,
                lineHeight: 1.6,
                fontSize: "0.875rem",
              }}
            >
              <Box
                component="strong"
                sx={{
                  fontWeight: 600,
                  color: theme.customColors.text.strong,
                }}
              >
                Note:
              </Box>{" "}
              {note}
            </Typography>
          </Box>
        )}
      </DialogContent>
      <DialogActions
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          px: 3,
          pb: 2.5,
          pt: 2,
          background: theme.customColors.background.cardHeader,
          borderTop: `1px solid ${theme.customColors.border.main}`,
          position: 'sticky',
          bottom: 0,
          zIndex: 2,
        }}
      >
        <Button
          onClick={onClose}
          variant="outlined"
          disabled={loading}
          size="small"
          sx={{
            fontWeight: 500,
            fontSize: "0.875rem",
            px: 2.5,
            py: 0.875,
            borderRadius: 1,
            borderColor: theme.customColors.border.main,
            color: theme.customColors.text.strong,
            "&:hover": {
              borderColor: theme.customColors.border.strong,
              backgroundColor: theme.customColors.background.hover,
              color: theme.customColors.text.strong,
            },
          }}
        >
          Cancel
        </Button>
        <LoadingButton
          onClick={onConfirm}
          variant="contained"
          loading={loading}
          disabled={confirmDisabled}
          size="small"
          sx={{
            fontWeight: 500,
            fontSize: "0.875rem",
            px: 2.5,
            py: 0.875,
            borderRadius: 1,
            color: theme.customColors.button.text,
            "&:hover": {
              bgcolor: theme.customColors.button.primary,
            },
            "&:disabled": {
              bgcolor: theme.customColors.background.light,
              color: theme.customColors.text.disabled,
            },
          }}
        >
          {buttonText}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};

export default ConfirmDialog;
